import type { OptionValue, Step, Substep } from "@views/Upholstery/types"
import type { PdmFlatRow } from "../../pdm/normalizePdm"
import {
  distinctSorted,
  extractDigitalAssetDetails,
  unwrapValue,
} from "../../pdm/normalizePdm"
import type { PdmAssetRecord } from "../../types"
import {
  getDatoSubstepOptionValues,
  overlayDatoRowAssetsOnPdmOptions,
  overlayDatoSkusOnPdmOptions,
  overlayDatoSkusViaPdmAssetRows,
} from "./pdm-option-sku"

export function slugifyPn(pn: string): string {
  return pn
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
}

/** Human year from YMM (first 4-digit run if needed). */
export function parseSelectedYear(yearParam: string): number {
  const n = Number(String(yearParam).trim())
  if (!Number.isNaN(n) && n > 1900 && n < 2100) {
    return n
  }

  const m = String(yearParam).match(/\d{4}/)
  if (m) {
    return Number(m[0])
  }
  return new Date().getFullYear()
}

/**
 * Build body/rows OptionValues from PDM labels.
 * - `slug` → `option-N` for factory-match card filtering (see factory-match-filter.ts)
 * - `sku` → left empty here; filled by overlayDatoSkusOnPdmOptions() from Dato for M3/custom SKU
 */
export function makeOptionFactories() {
  let seq = 0
  const mk = (displayLabel: string, group: string): OptionValue => {
    seq++
    const slug = `option-${seq}`
    return {
      id: `pdm-v2-${slugifyPn(group)}-${slug}`,
      optionName: displayLabel,
      sku: "",
      optionValue: displayLabel,
      slug,
      optionImage: null,
      compatibility: null,
    }
  }
  return { mk }
}

/**
 * Maps labels to option values. If labels contain comma-separated values
 * (e.g., "Manual, Power"), they are split into individual options.
 *
 * Row layouts can use a comma as wording, not as two options.
 * Pass `{ splitCommas: false }` for those.
 */
export function mapLabelsToOptions(
  labels: string[],
  mk: ReturnType<typeof makeOptionFactories>["mk"],
  group: string,
  options?: { splitCommas?: boolean }
): OptionValue[] {
  const splitCommas = options?.splitCommas !== false
  const expandedLabels = splitCommas
    ? labels.flatMap((label) =>
        label
          .split(",")
          .map((part) => part.trim())
          .filter(Boolean)
      )
    : labels.map((label) => label.trim()).filter(Boolean)
  return distinctSorted(expandedLabels).map((l) => mk(l, group))
}

export function buildRowOptionValues(
  pdmOpts: OptionValue[],
  datoRowsStep: Step | undefined,
  substepKey: string,
  rowType: string, // "Front Row", "Back Row", or "Middle Row"
  datoPdmAssets: PdmAssetRecord[] | undefined | null
): OptionValue[] {
  const datoOpts = getDatoSubstepOptionValues(datoRowsStep, substepKey)
  const withSkus = overlayDatoSkusOnPdmOptions(pdmOpts, datoOpts)
  const withRowSkus = overlayDatoSkusViaPdmAssetRows(
    withSkus,
    rowType,
    datoOpts,
    datoPdmAssets
  )
  return overlayDatoRowAssetsOnPdmOptions(withRowSkus, rowType, datoPdmAssets)
}

export function overlaySubsteps(
  template: Step,
  replacements: Partial<Record<string, OptionValue[]>>
): Partial<Step> {
  const nextSubs: Substep[] = template.substeps.map((ss) => {
    const ovs = replacements[ss.key]

    /** Keep Dato scaffold when no PDM-derived list for this substep. */
    if (!ovs || ovs.length === 0) {
      return ss
    }

    return {
      ...ss,
      category: {
        ...(ss.category ?? { optionValues: [], optionName: "" }),
        optionValues: ovs,
      },
    }
  })

  return { substeps: nextSubs }
}

/** One row per unique part_number — flattenMatchingApplications can repeat the same product per application. */
export function dedupeFlatRowsByPartNumber(
  flatRows: PdmFlatRow[]
): PdmFlatRow[] {
  const seen = new Map<string, PdmFlatRow>()
  for (const row of flatRows) {
    const partNumber = unwrapValue(
      row.product.part_number ?? row.product.PartNumber
    )
    if (!partNumber) continue
    const key = partNumber.toUpperCase()
    if (!seen.has(key)) {
      seen.set(key, row)
    }
  }
  return Array.from(seen.values())
}

/**
 * Factory match uses multiple PDM rows per SKU (one per digital_asset_type_code).
 * Dedupe by part_number + asset type so we keep Kit, Pattern, Tone, etc.
 */
export function dedupeFlatRowsForFactoryMatch(
  flatRows: PdmFlatRow[]
): PdmFlatRow[] {
  const seen = new Map<string, PdmFlatRow>()
  for (const row of flatRows) {
    const partNumber = unwrapValue(
      row.product.part_number ?? row.product.PartNumber
    )
    if (!partNumber) continue
    const assetType =
      extractDigitalAssetDetails(row.product).assetType || "unknown"
    const key = `${partNumber.toUpperCase()}|${assetType.toUpperCase()}`
    if (!seen.has(key)) {
      seen.set(key, row)
    }
  }
  return Array.from(seen.values())
}
